/**
 * Update a nav like component to scroll when there isn't enough room to show all nav items in a row.
 *
 * @param  {String} selector - Container that `querySelector` is un on to implement the scrolling on.
 *
 * @example Required Markup:
 *
 *      <nav class="{{ selector }}">
 *          <button type="button" class="scroll-btn scroll-backward"></button>
 *          <ul>
 *              <li><a class="active" href=""></a></li>
 *          </ul>
 *          <button type="button" class="scroll-btn scroll-forward"></button>
 *      </nav>
 *
 * @example Invocation:
 *
 *        createSubSectionScroll( '{{ selector }}' );
 */
export default ( selector ) => {

    if ( !document.querySelector( selector ) ) return;

    let isScrolling;

    let hasCleanedUp = false;
    const el = document.querySelector( selector );
    const subSectionContainer = el.querySelector( 'ul' );
    const scrollForwardButtton = el.querySelector( '.scroll-forward' );
    const scrollBackwardButtton = el.querySelector( '.scroll-backward' );
    const subSectionItems = subSectionContainer.querySelectorAll( 'li' );

    const handleResize = () => {

        hideButtons();

    };

    const handleScrolling = () => {

        window.clearTimeout( isScrolling );

        isScrolling = setTimeout( () => {

            hideButtons();

        }, 100 );

    };

    const handleActiveItemChange = ( event ) => {

        const el = event.target;

        if ( el.hasAttribute( 'role' ) && el.getAttribute( 'role' ) === 'tab' ) {

            scrollToActiveElement();
            el.focus();

        }

    };

    const scrollToFirstItem = () => {

        const firstItem = subSectionItems[ 0 ];

        subSectionContainer.scrollLeft = 0;

        firstItem.querySelector( 'a' ).focus();

        hideButtons();

    };

    const scrollToLastItem = () => {

        const lastItem = subSectionItems[ subSectionItems.length - 1 ];

        subSectionContainer.scrollLeft = subSectionContainer.scrollWidth - subSectionContainer.offsetWidth;

        lastItem.querySelector( 'a' ).focus();

        hideButtons();

    };

    const scrollToActiveElement = ( newActiveEl ) => {

        const activeEl = subSectionContainer.querySelector( '.active' ) && subSectionContainer.querySelector( '.active' ).parentNode;
        const activeElIndex = getActiveElementIndex();

        if ( activeEl ) {

            if ( activeElIndex === 0 ) {

                subSectionContainer.scrollLeft = 0;

            }
            else if ( activeElIndex === subSectionItems.length - 1 ) {

                subSectionContainer.scrollLeft = subSectionContainer.scrollWidth;

            }
            else {

                const boundingClientRect = activeEl.getBoundingClientRect();
                const scrollAmount = ( activeEl.offsetLeft - 45 ) - ( ( subSectionContainer.clientWidth / 2 ) - ( boundingClientRect.width / 2 ) );

                subSectionContainer.scrollLeft = scrollAmount;

            }

        }

    };

    const getActiveElementIndex = () => {

        let activeElIndex;

        subSectionItems.forEach( ( listItem, index ) => {

            if ( listItem.querySelector( '.active' ) ) {

                activeElIndex = index;

            }

        } );

        return activeElIndex;

    };

    const setupScrollNav = () => {

        scrollToActiveElement();

        setupEventListeners();

        hideButtons();

        window.addEventListener( 'resize', () => {

            handleResize();

        } );

    };

    const setupEventListeners = () => {

        scrollForwardButtton.addEventListener( 'click', scrollToLastItem );

        scrollBackwardButtton.addEventListener( 'click', scrollToFirstItem );

        subSectionContainer.addEventListener( 'touchend', hideButtons );

        subSectionContainer.addEventListener( 'scroll', handleScrolling );

        subSectionContainer.addEventListener( 'click', handleActiveItemChange );

    };

    const cleanupEventListeners = () => {

        scrollForwardButtton.removeEventListener( 'click', scrollToLastItem );

        scrollBackwardButtton.removeEventListener( 'click', scrollToFirstItem );

        subSectionContainer.addEventListener( 'touchend', hideButtons );

        subSectionContainer.addEventListener( 'scroll', handleScrolling );

    };

    const handleSetupCleanup = ( shouldRemoveEventListeners ) => {

        // Setup &/ Cleanup of Event listeners
        if ( shouldRemoveEventListeners && !hasCleanedUp ) {

            cleanupEventListeners();

            hasCleanedUp = true;

        }
        else if ( !shouldRemoveEventListeners && hasCleanedUp ) {

            setupEventListeners();

            hasCleanedUp = false;

        }

    };

    const updateClasses = ( shouldAddClassFwd, shouldAddClassBwd, shouldAddClassContainer ) => {

        const hasClassFwd = scrollForwardButtton.classList.contains( 'show-scroll' );
        const hasClassBwd = scrollBackwardButtton.classList.contains( 'show-scroll' );
        const hasClassContainer = subSectionContainer.classList.contains( 'scrollable' );

        // Forward Button
        if ( shouldAddClassFwd && !hasClassFwd ) {

            // Add class to forward button
            scrollForwardButtton.classList.add( 'show-scroll' );

        }
        else if ( !shouldAddClassFwd && hasClassFwd ) {

            // remove class from forward
            scrollForwardButtton.classList.remove( 'show-scroll' );

        }

        // Backward Button
        if ( shouldAddClassBwd && !hasClassBwd ) {

            // Add class to backward
            scrollBackwardButtton.classList.add( 'show-scroll' );

        }
        else if ( !shouldAddClassBwd && hasClassBwd ) {

            // Remove class
            scrollBackwardButtton.classList.remove( 'show-scroll' );

        }

        // Scroll Container
        if ( shouldAddClassContainer && !hasClassContainer ) {

            // Add Class
            subSectionContainer.classList.add( 'scrollable' );

        }
        else if ( !shouldAddClassContainer && hasClassContainer ) {

            // Remove class
            subSectionContainer.classList.remove( 'scrollable' );

        }

    };

    const hideButtons = () => {

        let shouldRemoveEventListeners = false;
        const totalScrollableWidth = subSectionContainer.scrollWidth;
        const visibleWidth = subSectionContainer.offsetWidth;
        const amountScrolled = subSectionContainer.scrollLeft;

        if ( totalScrollableWidth <= visibleWidth ) {

            // Enough room in nav, no need to scroll
            updateClasses( false, false, false );

            shouldRemoveEventListeners = true;

        }
        else if ( amountScrolled <= 0 ) {

            // Show only right scroll button
            updateClasses( true, false, true );

        }
        else if ( visibleWidth + amountScrolled >= totalScrollableWidth - 11 ) {

            // Show only left scroll button
            updateClasses( false, true, true );

        }
        else {

            // Show buttons on both sides
            updateClasses( true, true, true );

        }

        handleSetupCleanup( shouldRemoveEventListeners );

    };

    setupScrollNav();

};